import { JIRA_FEED_KINDS, type Channel, type JiraFeedKind, type JiraSubscription } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { useJira } from './JiraSettings';
import { Modal } from './RoomDialogs';
import { useSettings } from './SettingsDialog';

export const KIND_LABELS: Record<JiraFeedKind, string> = {
  created: 'Yeni iş',
  status: 'Durum değişikliği',
  assigned: 'Atama',
  commented: 'Yorum',
};

const field = 'h-[42px] rounded-md border border-line-strong bg-surface-2 px-3 text-base text-fg-1 focus:border-brand-400 focus:outline-none sm:text-[15px]';
const chip = 'rounded-xs bg-surface-3 px-1.5 py-0.5 text-[11.5px] font-semibold text-fg-2';

/** Channel menu › "Jira akışı" (docs/plans/2026-11-04-jira-design.md §3): which project's events land here. */
export function JiraFeedDialog({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const queries = useQueryClient();
  const jira = useJira();
  const connected = !!jira.data?.connection;
  const subscriptions = useQuery({ queryKey: ['jira-subscriptions', channel.id], queryFn: () => client.api.jiraSubscriptions(channel.id) });
  const projects = useQuery({ queryKey: ['jira-projects'], queryFn: () => client.api.jiraProjects(), enabled: connected, staleTime: 5 * 60_000 });
  const [projectKey, setProjectKey] = useState('');
  const [kinds, setKinds] = useState<JiraFeedKind[]>([...JIRA_FEED_KINDS]);
  const [allTypes, setAllTypes] = useState(true);
  const [types, setTypes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const project = projects.data?.find((p) => p.key === projectKey) ?? null;

  const saved = (list: JiraSubscription[]) => queries.setQueryData(['jira-subscriptions', channel.id], list);

  async function add(event: FormEvent) {
    event.preventDefault();
    if (!project) return setError('Bir proje seç.');
    if (kinds.length === 0) return setError('En az bir olay seç.');
    if (!allTypes && types.length === 0) return setError('En az bir iş türü seç ya da “Tüm türler”i işaretle.');
    setBusy(true);
    setError(null);
    try {
      saved(await client.api.addJiraSubscription(channel.id, { projectKey: project.key, kinds, issueTypes: allTypes ? null : types }));
      setProjectKey('');
      setAllTypes(true);
      setTypes([]);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      saved(await client.api.removeJiraSubscription(channel.id, id));
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  const toggle = <T,>(list: T[], value: T) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  return (
    <Modal title="Jira akışı" onClose={onClose}>
      {(close) => (
        <div className="flex flex-col gap-5">
          <p className="m-0 text-[13.5px] leading-normal text-fg-3">
            Seçtiğin projede olanlar bu kanala mesaj olarak düşer. Kanalın bildirim ayarı bu mesajlar için de geçerli.
          </p>

          <section aria-label="Abonelikler" className="flex flex-col gap-2">
            {subscriptions.isPending ? (
              <p className="m-0 text-sm text-fg-3">Yükleniyor…</p>
            ) : !subscriptions.data ? (
              <p className="m-0 text-sm text-[#EE9191]">{errorMessage(subscriptions.error)}</p>
            ) : subscriptions.data.length === 0 ? (
              <p className="m-0 text-sm text-fg-3">Bu kanala henüz Jira akışı bağlanmadı.</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {subscriptions.data.map((s) => (
                  <li key={s.id} className="flex items-start gap-3 rounded-md border border-line bg-surface-2 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">
                        {s.projectKey} · {s.projectName}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {s.kinds.map((k) => (
                          <span key={k} className={chip}>
                            {KIND_LABELS[k]}
                          </span>
                        ))}
                        <span className={chip}>{s.issueTypes ? s.issueTypes.join(', ') : 'Tüm türler'}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => void remove(s.id)}
                      aria-label={`${s.projectKey} akışını kaldır`}
                      className="nc-press h-8 flex-none rounded-sm border border-line-strong bg-surface-1 px-2.5 text-[13px] font-semibold text-[#EE9191]"
                    >
                      Kaldır
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {!connected ? (
            <div className="flex flex-col gap-2 rounded-md border border-line bg-surface-2 p-3">
              <p className="m-0 text-[13.5px] text-fg-2">Akış eklemek için Jira hesabını bağlamalısın; yalnızca Jira’da görebildiğin projeleri bağlayabilirsin.</p>
              <button
                type="button"
                onClick={() => {
                  close();
                  useSettings.getState().show('connections');
                }}
                className="nc-press h-9 self-start rounded-md border-0 bg-brand-500 px-3 text-sm font-bold text-white"
              >
                Jira hesabını bağla
              </button>
            </div>
          ) : (
            <form onSubmit={(e) => void add(e)} className="flex flex-col gap-3" noValidate>
              <h3 className="m-0 text-[13px] font-bold tracking-[0.06em] text-fg-3 uppercase">Akış ekle</h3>
              <label className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-fg-2">Proje</span>
                <select
                  value={projectKey}
                  onChange={(e) => {
                    setProjectKey(e.target.value);
                    setTypes([]);
                  }}
                  className={field}
                >
                  <option value="">{projects.isPending ? 'Yükleniyor…' : 'Proje seç'}</option>
                  {projects.data?.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.key} · {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
                <legend className="mb-1.5 p-0 text-[13px] font-semibold text-fg-2">Olaylar</legend>
                <div className="grid grid-cols-2 gap-1.5">
                  {JIRA_FEED_KINDS.map((k) => (
                    <label key={k} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input type="checkbox" checked={kinds.includes(k)} onChange={() => setKinds((list) => toggle(list, k))} className="size-4 accent-brand-500" />
                      {KIND_LABELS[k]}
                    </label>
                  ))}
                </div>
              </fieldset>
              {project && project.issueTypes.length > 1 && (
                <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
                  <legend className="mb-1.5 p-0 text-[13px] font-semibold text-fg-2">İş türleri</legend>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" checked={allTypes} onChange={(e) => setAllTypes(e.target.checked)} className="size-4 accent-brand-500" />
                    Tüm türler
                  </label>
                  {!allTypes && (
                    <div className="grid grid-cols-2 gap-1.5 pl-6">
                      {project.issueTypes.map((t) => (
                        <label key={t} className="flex cursor-pointer items-center gap-2 text-sm">
                          <input type="checkbox" checked={types.includes(t)} onChange={() => setTypes((list) => toggle(list, t))} className="size-4 accent-brand-500" />
                          <span className="truncate">{t}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>
              )}
              {error && (
                <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">
                  {error}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={close} className="nc-press h-10 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold">
                  Kapat
                </button>
                <button type="submit" disabled={busy} className="nc-press nc-cta h-10 rounded-md border-0 bg-brand-500 px-4 text-sm font-bold text-white disabled:opacity-60">
                  Akışı ekle
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </Modal>
  );
}
